import React, { useEffect, useState } from "react";
import { MetaTags } from "react-meta-tags";
import PostsByTags from "../components/Home/PostsByTags";
import FollowedTags from "../components/Home/FollowedTags";
import FollowedBlogs from "../components/Home/FollowedBlogs";
import PopularTags from "../components/Home/PopularTags";
import PopularBlogs from "../components/Home/PopularBlogs";
import PopularKeywords from "../components/Home/PopularKeywords";
import Loader from "../components/mini/Loader";
import { useAuthStore } from "../store/AuthStore";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPostsAPI_v2 } from "../api/GetPosts";
import SubHeader from "../components/medium/SubHeader";
import { scrollTo } from "../utils/helper";
import { Switch } from "antd";
import Paginator from "../components/medium/Paginator";
import ArticleCard from "../components/medium/ArticleCard";
import "../styles/home.scss";
import { useDispatch } from "react-redux";
import { createBlogModal } from "../redux/actions/blogAction";

const Home = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { isLoggedIn, hasBlog, isPhysician, mainBlogId } = useAuthStore();

  const [currentFilter, setCurrentFilter] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);

  //Page History
  useEffect(() => {
    sessionStorage.setItem("LastVisitedURL", pathname);

    const pageHistory = sessionStorage.getItem("HomePageNumber");
    const filterHistory = sessionStorage.getItem("HomePageFilter");

    if (pageHistory) {
      setCurrentPage(parseInt(pageHistory));
    }
    if (filterHistory) {
      setCurrentFilter(filterHistory);
    }
  }, [pathname]);

  const { isLoading, data: blogPosts } = useQuery({
    queryKey: ["GetBlogPosts", currentPage, currentFilter],
    queryFn: () =>
      getPostsAPI_v2({
        size: 10,
        page: currentPage,
        category: currentFilter,
      }),
    select: ({ data }) => data.data,
  });

  const handlePagination = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem("HomePageNumber", page.toString());

    scrollTo();
  };

  const createPost = () => {
    history.push("/createPost/" + mainBlogId + "/post/new");
  };

  const createBlog = () => {
    dispatch(createBlogModal(true));
  };

  const goToSearch = () => {
    history.push("/search/1/posts");
  };

  const handleSwitchChange = (isPopular) => {
    const filter = isPopular ? "popular" : "recent";

    setCurrentFilter(filter);
    setCurrentPage(1);

    sessionStorage.setItem("HomePageFilter", filter);
    sessionStorage.setItem("HomePageNumber", "1");
  };

  return (
    <>
      <MetaTags>
        <title>Physician Blogs - DoveMed</title>
      </MetaTags>

      {/* Top Banner */}
      <SubHeader
        title="Physician Blogs"
        description="DoveMed offers a platform for verified physicians to write on a variety of health topics, about their professional experiences, personal stories, medical innovations, research of interests, and other issues of life."
        buttonName={
          isLoggedIn
            ? isPhysician && hasBlog
              ? "CREATE POST"
              : isPhysician
              ? "CREATE BLOG"
              : "SEARCH"
            : "SEARCH"
        }
        buttonClicked={
          isLoggedIn
            ? isPhysician && hasBlog
              ? createPost
              : isPhysician
              ? createBlog
              : goToSearch
            : goToSearch
        }
        isLoggedIn={isLoggedIn}
      />

      {/* Switch */}
      <div className="home-container">
        <div className="home-left-container">
          {/* Header */}
          <div className="md:flex items-center justify-between mb-6">
            <h2 className="section-title">Most {currentFilter} Articles</h2>
            <p className="ml-auto">
              <span style={{ margin: "5px" }}>Recent</span>
              <Switch onChange={handleSwitchChange} />
              <span style={{ margin: "5px" }}>Popular</span>
            </p>
          </div>

          {/* Main */}
          {isLoading ? (
            <Loader />
          ) : blogPosts?.posts?.length > 0 ? (
            <>
              {blogPosts?.posts.map((post) => (
                <div key={post.id} style={{ marginBottom: 30 }}>
                  <ArticleCard post={post} isBookmarkTab={false} />
                </div>
              ))}
              <div className="my-24 mx-auto">
                <Paginator
                  count={blogPosts?.count}
                  currentPage={currentPage}
                  pageChange={handlePagination}
                />
              </div>
            </>
          ) : (
            <div
              style={{
                display: "grid",
                placeItems: "center",
                height: "35vh",
              }}
            >
              <h2 className="section-title">No Posts Found</h2>
            </div>
          )}
        </div>

        <div className="home-right-container">
          {isLoggedIn && <PostsByTags />}
          <FollowedTags />
          {isLoggedIn && <FollowedBlogs />}
          <PopularTags />
          <PopularKeywords />
          <PopularBlogs />
        </div>
      </div>

      {/* Bottom Banner */}
      {isLoggedIn && (
        <SubHeader
          title={isPhysician ? "Share a new article" : "Are you a physician?"}
          description="If you are a physician, please click below to login to Physician Blogs and experience a new world of Blogs."
          buttonName={isPhysician ? "CREATE POST" : "CLICK HERE"}
          buttonClicked={
            isPhysician ? createPost : () => window.open("/", "_blank")
          }
          isLoggedIn={isLoggedIn}
        />
      )}
    </>
  );
};

export default Home;
